import { format } from "date-fns";
import { enUS, es, ptBR, ru } from "date-fns/locale";
import type { Locale } from "date-fns";

const DATE_FNS_LOCALES: Record<string, Locale> = {
  en: enUS,
  ru,
  es,
  pt: ptBR,
};

const SHORT_DATE_PATTERN: Record<string, string> = {
  ru: "dd.MM.yyyy",
  es: "dd.MM.yyyy",
  en: "MM/dd/yyyy",
  pt: "dd/MM/yyyy",
};

const LONG_DATE_PATTERN: Record<string, string> = {
  ru: "d MMMM yyyy",
  es: "d 'de' MMMM 'de' yyyy",
  en: "MMMM d, yyyy",
  pt: "d 'de' MMMM 'de' yyyy",
};

const TIME_PATTERN: Record<string, string> = {
  ru: "HH:mm",
  es: "HH:mm",
  en: "h:mm a",
  pt: "HH:mm",
};

// "24 апр 2026" / "Apr 24, 2026" — dense date for cards that can't fit the
// full long form.
const SHORT_MONTH_DAY_YEAR_PATTERN: Record<string, string> = {
  ru: "d MMM yyyy",
  es: "d MMM yyyy",
  en: "MMM d, yyyy",
  pt: "d MMM yyyy",
};

// Same family without the year, for surfaces where the year is implied
// by context (e.g. a "Today" / "This week" group header).
const SHORT_MONTH_DAY_PATTERN: Record<string, string> = {
  ru: "d MMM",
  es: "d MMM",
  en: "MMM d",
  pt: "d MMM",
};

const FALLBACK_PATTERN = "yyyy-MM-dd";

function shortLng(lng: string): string {
  return lng.split("-")[0]?.toLowerCase() ?? "en";
}

function resolveDateLocale(lng: string): Locale {
  return DATE_FNS_LOCALES[shortLng(lng)] ?? enUS;
}

function pickPattern(map: Record<string, string>, lng: string): string {
  return map[shortLng(lng)] ?? map.en ?? FALLBACK_PATTERN;
}

// Intl emits NBSP (U+00A0) or NNBSP (U+202F) as the thousands separator for
// ru / fr / es, depending on the host's CLDR version. We collapse both to a
// regular space so the rendered string is identical across Node/V8/CI versions
// and copies cleanly out of the mini app.
function normalizeWhitespace(value: string): string {
  return value.replace(/[\u00A0\u202F]/g, " ");
}

export function formatDate(date: Date, pattern: string, lng: string): string {
  return format(date, pattern, { locale: resolveDateLocale(lng) });
}

export function formatShortDate(date: Date, lng: string): string {
  return formatDate(date, pickPattern(SHORT_DATE_PATTERN, lng), lng);
}

export function formatLongDate(date: Date, lng: string): string {
  return formatDate(date, pickPattern(LONG_DATE_PATTERN, lng), lng);
}

export function formatTime(date: Date, lng: string): string {
  return formatDate(date, pickPattern(TIME_PATTERN, lng), lng);
}

export function formatShortMonthDayYear(date: Date, lng: string): string {
  return formatDate(date, pickPattern(SHORT_MONTH_DAY_YEAR_PATTERN, lng), lng);
}

export function formatShortMonthDay(date: Date, lng: string): string {
  return formatDate(date, pickPattern(SHORT_MONTH_DAY_PATTERN, lng), lng);
}

// Force grouping for 4-digit numbers — by CLDR default es/de/etc only group
// at 5+ digits ("1000" vs "10.000"), but we want a consistent visual for
// star balances and friend counts that frequently sit in the 1000–9999
// range. The string variant of `useGrouping` shipped in ES2023 but is not
// yet typed in our `lib.es2023.intl.d.ts` baseline, so we cast.
const ALWAYS_GROUP = { useGrouping: "always" } as unknown as Intl.NumberFormatOptions;

export function formatNumber(n: number, lng: string): string {
  return normalizeWhitespace(
    new Intl.NumberFormat(shortLng(lng), ALWAYS_GROUP).format(n),
  );
}

export function formatStars(n: number, lng: string): string {
  return `${formatNumber(n, lng)} ★`;
}

export function formatPercent(
  n: number,
  lng: string,
  fractionDigits = 1,
): string {
  const num = normalizeWhitespace(
    new Intl.NumberFormat(shortLng(lng), {
      minimumFractionDigits: 0,
      maximumFractionDigits: fractionDigits,
      ...ALWAYS_GROUP,
    }).format(n),
  );
  return `${num}%`;
}

export function formatCurrencyUSD(n: number, lng: string): string {
  return normalizeWhitespace(
    new Intl.NumberFormat(shortLng(lng), {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(n),
  );
}

export function formatSignedCurrencyUSD(n: number, lng: string): string {
  if (n === 0) return formatCurrencyUSD(0, lng);
  if (n > 0) return `+${formatCurrencyUSD(n, lng)}`;
  return `−${formatCurrencyUSD(Math.abs(n), lng)}`;
}
